import { useEffect, useMemo, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { AlertCircle, Pencil, TrendingDown, TrendingUp } from 'lucide-react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip as RTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { format, parseISO } from 'date-fns';
import { toast } from 'sonner';
import { apiFetch } from '../utils/api';

const RUPEE_ICON = '/icons/cost-rupee.png';
const TEAL = '#2EB4AC';
const TEAL_DARK = '#259993';

// Compact number formatter (1.3k, 12.4k, etc.)
const compact = (n) => {
  if (n === null || n === undefined || Number.isNaN(n)) return '0';
  const num = Number(n);
  if (Math.abs(num) >= 1000) return (num / 1000).toFixed(num % 1000 === 0 ? 0 : 1) + 'k';
  return String(Math.round(num));
};

const formatINR = (n) =>
  new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(Math.round(Number(n) || 0));

const lastMondayMidnight = () => {
  const now = new Date();
  const daysSinceMon = (now.getDay() + 6) % 7; // Mon=0 ... Sun=6
  const d = new Date(now);
  d.setDate(now.getDate() - daysSinceMon);
  d.setHours(0, 0, 0, 0);
  return d;
};

// Tiny inline sparkline SVG over historical credits
const Sparkline = ({ history }) => {
  if (!history || history.length < 2) return null;
  const w = 56;
  const h = 18;
  const pad = 2;
  const credits = history.map((p) => Number(p.credits) || 0);
  const max = Math.max(...credits);
  const min = Math.min(...credits);
  const range = max - min || 1;
  const stepX = (w - pad * 2) / (credits.length - 1);
  const points = credits
    .map((c, i) => {
      const x = pad + i * stepX;
      const y = pad + (h - pad * 2) * (1 - (c - min) / range);
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');
  const lastX = pad + (credits.length - 1) * stepX;
  const lastY = pad + (h - pad * 2) * (1 - (credits[credits.length - 1] - min) / range);
  return (
    <svg width={w} height={h} className="shrink-0" aria-hidden="true">
      <polyline fill="none" stroke={TEAL} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" points={points} />
      <circle cx={lastX} cy={lastY} r="2" fill="white" stroke={TEAL} strokeWidth="1.5" />
    </svg>
  );
};

export default function CostTrackerWidget() {
  const [state, setState] = useState(null);
  const [history, setHistory] = useState([]);
  const [expanded, setExpanded] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [creditsInput, setCreditsInput] = useState('');
  const [rateInput, setRateInput] = useState('');
  const [saving, setSaving] = useState(false);
  const pillRef = useRef(null);

  const fetchAll = async () => {
    try {
      const [sRes, hRes] = await Promise.all([
        apiFetch('/api/cost-tracker'),
        apiFetch('/api/cost-tracker/history?weeks=12'),
      ]);
      if (sRes.ok) setState(await sRes.json());
      if (hRes.ok) {
        const data = await hRes.json();
        setHistory(data.history || []);
      }
    } catch (err) {
      console.error('Cost tracker fetch failed', err);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  // Click-outside to collapse the pill
  useEffect(() => {
    if (!expanded) return;
    const handler = (e) => {
      if (pillRef.current && !pillRef.current.contains(e.target)) setExpanded(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [expanded]);

  const stale = useMemo(() => {
    if (!state?.updated_at) return true;
    return new Date(state.updated_at) < lastMondayMidnight();
  }, [state]);

  const credits = Number(state?.total_credits || 0);
  const rate = Number(state?.cost_per_credit_inr || 0);
  const totalSpent = credits * rate;

  const trendDelta = useMemo(() => {
    if (!history || history.length < 2) return null;
    const prev = Number(history[history.length - 2].credits) || 0;
    const curr = Number(history[history.length - 1].credits) || 0;
    const diff = curr - prev;
    const pct = prev > 0 ? Math.round((diff / prev) * 100) : null;
    return { diff, pct, up: diff > 0, down: diff < 0 };
  }, [history]);

  const openDialog = () => {
    setCreditsInput(state?.total_credits != null ? String(state.total_credits) : '');
    setRateInput(state?.cost_per_credit_inr != null ? String(state.cost_per_credit_inr) : '13.70');
    setDialogOpen(true);
  };

  const liveTotal = useMemo(() => {
    const c = parseFloat(creditsInput);
    const r = parseFloat(rateInput);
    if (Number.isNaN(c) || Number.isNaN(r)) return null;
    return c * r;
  }, [creditsInput, rateInput]);

  const handleSave = async () => {
    const c = parseFloat(creditsInput);
    const r = parseFloat(rateInput);
    if (Number.isNaN(c) || Number.isNaN(r)) {
      toast.error('Please enter valid numbers');
      return;
    }
    setSaving(true);
    try {
      const res = await apiFetch('/api/cost-tracker', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ total_credits: c, cost_per_credit_inr: r }),
      });
      if (!res.ok) throw new Error('Save failed');
      setState(await res.json());
      const hRes = await apiFetch('/api/cost-tracker/history?weeks=12');
      if (hRes.ok) setHistory((await hRes.json()).history || []);
      toast.success('Cost tracker updated');
      setDialogOpen(false);
      setExpanded(false);
    } catch {
      toast.error('Could not save — please try again');
    } finally {
      setSaving(false);
    }
  };

  const lastUpdatedText = useMemo(() => {
    if (!state?.updated_at) return 'Never updated';
    try {
      const d = parseISO(state.updated_at);
      const who = state.updated_by ? state.updated_by.split('@')[0] : 'system';
      return `Last updated ${format(d, 'PPp')} by ${who}`;
    } catch {
      return 'Last updated recently';
    }
  }, [state]);

  // Chart data: use ISO -> Date for X axis
  const chartData = useMemo(
    () =>
      (history || []).map((h) => ({
        ...h,
        date: parseISO(h.week_start),
      })),
    [history],
  );

  return (
    <>
      {/* Collapsed icon-only state */}
      {!expanded && (
        <Button
          variant="ghost"
          size="icon"
          className="h-9 w-9 relative"
          onClick={() => setExpanded(true)}
          data-testid="cost-tracker-icon"
          aria-label="Cost tracker"
        >
          <img
            src={RUPEE_ICON}
            alt=""
            className="h-5 w-5 hover:scale-110 transition-transform object-contain"
          />
          {stale && (
            <span className="absolute top-1 right-1 flex h-2 w-2" data-testid="cost-tracker-stale-dot">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-500 opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-red-500 ring-1 ring-white" />
            </span>
          )}
        </Button>
      )}

      {/* Expanded pill state */}
      {expanded && (
        <div
          ref={pillRef}
          className="group h-9 rounded-xl border border-slate-200 dark:border-border bg-white dark:bg-card flex items-center overflow-hidden cursor-pointer animate-in fade-in slide-in-from-right-2 duration-200 shadow-sm hover:shadow-md transition-shadow"
          onClick={openDialog}
          data-testid="cost-tracker-pill"
        >
          {/* Left cell: credits */}
          <div className="flex items-center gap-1.5 px-2.5 py-1 bg-slate-50/70 dark:bg-accent/30 h-full">
            <img src={RUPEE_ICON} alt="" className="h-3.5 w-3.5 object-contain" />
            <div className="flex flex-col leading-none">
              <span className="text-[9px] font-semibold uppercase text-slate-400 tracking-wider">Credits</span>
              <span className="text-xs font-bold tabular-nums" data-testid="cost-tracker-credits">{compact(credits)}</span>
            </div>
          </div>
          {/* Divider */}
          <div className="h-5 w-px bg-slate-200 dark:bg-border" />
          {/* Right cell: spent */}
          <div className="flex items-center gap-2 px-2.5 py-1 h-full">
            <div className="flex flex-col leading-none">
              <span className="text-[9px] font-semibold uppercase tracking-wider" style={{ color: TEAL }}>Spent</span>
              <span className="text-xs font-bold tabular-nums" data-testid="cost-tracker-spent">₹{formatINR(totalSpent)}</span>
            </div>
            <Sparkline history={history} />
            {stale ? (
              <span className="flex h-2 w-2 relative" data-testid="cost-tracker-stale-dot-pill">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-500 opacity-75" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-red-500 ring-1 ring-white" />
              </span>
            ) : (
              <Pencil className="h-3 w-3 text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity" />
            )}
          </div>
        </div>
      )}

      {/* Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="cost-tracker-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <img src={RUPEE_ICON} alt="" className="h-5 w-5 object-contain" />
              App Development Cost Tracker
            </DialogTitle>
            <DialogDescription className="text-xs">
              Log credits consumed and the per-credit cost. Update weekly to build a trendline.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 pt-2">
            {stale && (
              <div
                className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900"
                data-testid="cost-tracker-stale-alert"
              >
                <AlertCircle className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                <p className="text-xs text-amber-800 dark:text-amber-200 leading-relaxed">
                  It's a new week — please update the credits consumed since last Monday.
                </p>
              </div>
            )}

            {chartData.length >= 2 ? (
              <div className="rounded-xl border border-slate-200 dark:border-border p-3" data-testid="cost-tracker-chart">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500 dark:text-muted-foreground">
                      Week-on-Week Credits
                    </p>
                    <p className="text-[10px] text-slate-400 dark:text-muted-foreground/70 mt-0.5">
                      Last {chartData.length} weeks · Mondays
                    </p>
                  </div>
                  {trendDelta && (trendDelta.up || trendDelta.down) && (
                    <span
                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                        trendDelta.up ? 'bg-red-50 text-red-600 dark:bg-red-950/40 dark:text-red-400'
                                       : 'bg-emerald-50 text-emerald-600 dark:bg-emerald-950/40 dark:text-emerald-400'
                      }`}
                      data-testid="cost-tracker-trend-chip"
                    >
                      {trendDelta.up ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                      {trendDelta.diff > 0 ? '+' : ''}{compact(trendDelta.diff)} cr
                      {trendDelta.pct !== null ? ` (${trendDelta.diff > 0 ? '+' : ''}${trendDelta.pct}%)` : ''}
                    </span>
                  )}
                </div>
                <div className="h-44">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={chartData} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                      <defs>
                        <linearGradient id="ctGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor={TEAL} stopOpacity={0.35} />
                          <stop offset="100%" stopColor={TEAL} stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
                      <XAxis
                        dataKey="date"
                        tickFormatter={(d) => format(d, 'd MMM')}
                        stroke="hsl(var(--muted-foreground))"
                        fontSize={10}
                        tickLine={false}
                        axisLine={false}
                      />
                      <YAxis
                        tickFormatter={(v) => compact(v)}
                        stroke="hsl(var(--muted-foreground))"
                        fontSize={10}
                        tickLine={false}
                        axisLine={false}
                        width={40}
                      />
                      <RTooltip
                        contentStyle={{ borderRadius: 12, border: '1px solid hsl(var(--border))', background: 'hsl(var(--popover))', fontSize: 11 }}
                        labelFormatter={(d) => `Week of ${format(d, 'd MMM yyyy')}`}
                        formatter={(value) => [`${compact(value)} cr`, 'Credits']}
                      />
                      <Area
                        type="monotone"
                        dataKey="credits"
                        stroke={TEAL}
                        strokeWidth={2}
                        fill="url(#ctGradient)"
                        dot={{ r: 3, fill: 'white', stroke: TEAL, strokeWidth: 2 }}
                        activeDot={{ r: 4, fill: TEAL, stroke: 'white', strokeWidth: 2 }}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>
            ) : (
              <div
                className="rounded-xl border border-dashed border-slate-300 dark:border-border p-4 text-center"
                data-testid="cost-tracker-chart-empty"
              >
                <p className="text-xs text-slate-500 dark:text-muted-foreground">
                  Save updates each week to build the trendline.
                </p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="ct-credits" className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 dark:text-muted-foreground">
                  Total Credits
                </Label>
                <Input
                  id="ct-credits"
                  type="number"
                  step="any"
                  min="0"
                  placeholder="0"
                  value={creditsInput}
                  onChange={(e) => setCreditsInput(e.target.value)}
                  data-testid="cost-tracker-credits-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="ct-rate" className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 dark:text-muted-foreground">
                  Per Credit ₹
                </Label>
                <Input
                  id="ct-rate"
                  type="number"
                  step="any"
                  min="0"
                  placeholder="13.70"
                  value={rateInput}
                  onChange={(e) => setRateInput(e.target.value)}
                  data-testid="cost-tracker-rate-input"
                />
                <p className="text-[10px] text-slate-400 dark:text-muted-foreground/70 leading-tight">
                  Default ₹13.70 — edit anytime as billing changes.
                </p>
              </div>
            </div>

            <div className="rounded-lg bg-slate-50/60 dark:bg-accent/20 border border-slate-200 dark:border-border p-3" data-testid="cost-tracker-live-preview">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 dark:text-muted-foreground mb-0.5">
                Total spent
              </p>
              <p className="text-2xl font-bold tabular-nums">
                ₹{liveTotal !== null ? formatINR(liveTotal) : '—'}
              </p>
            </div>

            <p className="text-[10px] text-slate-400 dark:text-muted-foreground/70" data-testid="cost-tracker-last-updated">
              {lastUpdatedText}
            </p>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setDialogOpen(false)} data-testid="cost-tracker-cancel-btn">
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="text-white"
              style={{ backgroundColor: TEAL }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = TEAL_DARK)}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = TEAL)}
              data-testid="cost-tracker-save-btn"
            >
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
